import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MARKETING_URL } from '../config/marketingSite.js';

/**
 * Encapsulates the "Back to marketing" navigation decision so every
 * auth page — email, OTP, onboarding — behaves identically.
 *
 * Decision (tightened for origin safety):
 *
 *   1. Parse the configured marketing URL into a URL object. If it's
 *      malformed we cannot origin-match anything, so we skip straight
 *      to the fallback branch.
 *
 *   2. Parse `document.referrer` into a URL object. Malformed or empty
 *      referrers cannot origin-match — they mean the user opened the
 *      tab directly, or the referrer header was withheld by policy.
 *
 *   3. Use `navigate(-1)` ONLY when BOTH:
 *        - `window.history` has more than one entry (a real prior page
 *          to pop back to), AND
 *        - the referrer's origin matches the marketing origin exactly.
 *
 *      Anything else — an unrelated external referrer (e.g. Google
 *      search), a matching referrer with a shallow history, or a
 *      parse failure — falls through to the fallback so we never send
 *      the user to a random third-party page.
 *
 *   4. Fallback: full-page navigation to the configured marketing
 *      homepage via `window.location.assign` (not `replace`, so the
 *      user can browser-back from the marketing page and land on
 *      /auth again if that was accidental).
 *
 * The OTP flow's local state is never touched — this hook only decides
 * where to navigate. Any in-progress OTP is abandoned by the
 * navigation itself, exactly as it would be for any other exit.
 */
export default function useMarketingBack({ marketingUrl = MARKETING_URL } = {}) {
    const navigate = useNavigate();

    const handleBack = useCallback(() => {
        // Parse the marketing URL up front. A malformed configured
        // value means we cannot safely origin-match, so the only
        // correct action is to fall back to `assign(marketingUrl)`
        // and let the browser surface any parse error to the user
        // (rather than silently sending them somewhere else).
        let marketingOrigin = null;
        try {
            marketingOrigin = new URL(marketingUrl).origin;
        } catch {
            marketingOrigin = null;
        }

        // Parse the referrer defensively. An empty string, an opaque
        // origin ("null"), or any garbage that isn't a URL all end up
        // as `null` here — the same "no usable referrer" bucket.
        let referrerOrigin = null;
        const rawReferrer = typeof document !== 'undefined' ? document.referrer : '';
        if (rawReferrer) {
            try {
                referrerOrigin = new URL(rawReferrer).origin;
            } catch {
                referrerOrigin = null;
            }
        }

        const hasHistory =
            typeof window !== 'undefined'
            && typeof window.history !== 'undefined'
            && window.history.length > 1;

        // Strict origin equality on the URL.origin string — protocol,
        // host, and port must all match. A different subdomain (www.
        // vs. bare) counts as a different origin by design; broaden
        // the config if a deployment needs it, don't relax the match.
        const referrerMatchesMarketing =
            marketingOrigin !== null
            && referrerOrigin !== null
            && referrerOrigin === marketingOrigin;

        if (hasHistory && referrerMatchesMarketing) {
            navigate(-1);
            return;
        }

        // Fallback: external redirect to the configured marketing
        // homepage. Always safe — even a malformed marketingUrl at
        // this point yields a fetch error the user can act on rather
        // than being dropped on an unrelated site.
        if (typeof window !== 'undefined') {
            window.location.assign(marketingUrl);
        }
    }, [navigate, marketingUrl]);

    return { handleBack, marketingUrl };
}
